import { useState } from "react";
import { UserPlus, X, Loader2, Check } from "lucide-react";
import { api } from "../lib/nailApi";

/**
 * Phase 8.12.1 — AddWalkInModal
 *
 * Door-day walk-in creation form for the admin/organizer. Calls the existing
 * fail-closed `POST /api/nail-events/{slug}/checkin/walkin` endpoint, which
 * creates a row with `is_walkin=true, checked_in=true`. Requires the global
 * check-in PIN via `X-Checkin-Pin` header (default 2468 unless the event
 * overrides with its per-event PIN).
 */
export default function AddWalkInModal({ slug, onClose, onCreated, lang = "vi", checkinPin = "2468" }) {
  const L = (vi, en) => (lang === "en" ? en : vi);
  const [form, setForm] = useState({
    business_name: "",
    owner_name: "",
    phone: "",
    email: "",
    guest_count: 1,
    preferred_language: lang === "en" ? "en" : "vi",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pin, setPin] = useState(checkinPin || "");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!form.business_name.trim()) return setError(L("Vui lòng nhập tên tiệm.", "Business name is required."));
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 10)
      return setError(L("Số điện thoại phải đủ 10 chữ số.", "Phone must be 10 digits."));
    if (!pin.trim()) return setError(L("Mã PIN check-in không đúng.", "Missing check-in PIN."));

    setBusy(true);
    try {
      const { data } = await api.post(
        `/nail-events/${slug}/checkin/walkin`,
        {
          business_name: form.business_name.trim(),
          owner_name: form.owner_name.trim() || undefined,
          phone: form.phone.trim(),
          email: form.email.trim() || undefined,
          guest_count: Math.max(1, Number(form.guest_count) || 1),
          preferred_language: form.preferred_language,
        },
        { headers: { "X-Checkin-Pin": pin.trim() } }
      );
      onCreated?.(data);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || L("Có lỗi xảy ra.", "Something went wrong."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-walkin-modal"
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center px-3 sm:px-6 py-3 sm:py-8 bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-espresso/10 bg-linen-100">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald2 text-white">
              <UserPlus className="w-4 h-4" />
            </span>
            <div>
              <div className="font-serif text-lg text-espresso leading-tight">
                {L("Thêm Walk-in", "Add Walk-in")}
              </div>
              <div className="text-[11px] text-espresso-soft">
                {L("Khách tới cửa chưa đăng ký trước", "Guest arriving without pre-registration")}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="walkin-close"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-linen-200 text-espresso-soft"
            aria-label={L("Đóng", "Close")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-3">
          <Field label={L("Tên tiệm nail *", "Business name *")}>
            <input
              type="text"
              value={form.business_name}
              onChange={(e) => update("business_name", e.target.value)}
              data-testid="walkin-business-name"
              autoFocus
              className="w-full bg-white border border-espresso/15 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2"
            />
          </Field>
          <Field label={L("Tên chủ tiệm", "Owner name")}>
            <input
              type="text"
              value={form.owner_name}
              onChange={(e) => update("owner_name", e.target.value)}
              data-testid="walkin-owner-name"
              className="w-full bg-white border border-espresso/15 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={L("Điện thoại *", "Phone *")}>
              <input
                type="tel"
                inputMode="tel"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                data-testid="walkin-phone"
                placeholder="(408) 555-1234"
                className="w-full bg-white border border-espresso/15 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2"
              />
            </Field>
            <Field label={L("Số khách", "Guest count")}>
              <input
                type="number"
                min={1}
                max={20}
                value={form.guest_count}
                onChange={(e) => update("guest_count", e.target.value)}
                data-testid="walkin-guest-count"
                className="w-full bg-white border border-espresso/15 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2"
              />
            </Field>
          </div>
          <Field label={L("Email (không bắt buộc)", "Email (optional)")}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              data-testid="walkin-email"
              className="w-full bg-white border border-espresso/15 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2"
            />
          </Field>

          {/* PIN — hidden by default (uses default), reveal on click if needed */}
          <details className="text-[11px] text-espresso-soft">
            <summary className="cursor-pointer hover:text-espresso">
              {L("Mã PIN check-in", "Check-in PIN")}
            </summary>
            <input
              type="text"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              data-testid="walkin-pin"
              className="mt-2 w-full bg-white border border-espresso/15 rounded-xl px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald2"
            />
          </details>

          {error && (
            <div
              data-testid="walkin-error"
              className="rounded-xl bg-[#FBEDEB] border border-[#E9C6C1] text-[#7A2E2E] text-sm px-3 py-2"
            >
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 bg-linen-100 border-t border-espresso/10 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="walkin-cancel"
            className="px-4 py-2 text-sm font-medium text-espresso-soft hover:text-espresso rounded-full transition"
          >
            {L("Hủy", "Cancel")}
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="walkin-submit"
            className="inline-flex items-center gap-1.5 bg-emerald2 text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-emerald2-dark disabled:opacity-50 transition"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {L("Thêm & Check-in", "Add & Check in")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
